/**
 * Die Plattform, wie sie der Nutzer sieht.
 *
 * Drei Flaechen, EINE Anwendung. Bis zum 22.09.2026 waren DAM
 * und Brand-Portal eine eigene Next-Anwendung auf Directus, die
 * hinter `/assets` am Agenten hing; seit der Entscheidung fuer
 * Variante A (`docs/UEBERGABE.md`) sind es Bereiche des Agenten
 * auf derselben Datenbank. Ein Ursprung, eine Sitzung, ein
 * Datenmodell.
 *
 * Geblieben ist der Modulwechsel in der Kopfzeile: DAM und
 * Brand-Portal sind zwei Arbeiten an derselben Kundeninstanz und
 * keine Bereiche wie Rechnungen oder Projekte. Welche Instanz
 * gemeint ist, steht daneben in der Adresse: /dam/<kennung>.
 *
 * Die Kunden-Auftritte laufen spaeter auf eigenen Domains gegen
 * dieselben Routen; welcher Mandant gemeint ist, entscheidet
 * dann der Host.
 */

export type SurfaceId = "agent" | "assets" | "brand";

export type Surface = {
  id: SurfaceId;
  /** So heisst die Flaeche in der Oberflaeche. */
  label: string;
  /** Kurzform fuer den Modulwechsler in der Kopfzeile, wo drei
   *  Namen nebeneinander stehen und jeder Buchstabe zaehlt. */
  short: string;
  /** Ein Satz, der sagt, wofuer sie da ist. */
  description: string;
  /** Pfad im gemeinsamen Ursprung — absolut, app-uebergreifend. */
  href: string;
  /** Kuerzel fuer die Umschalter-Kachel. */
  initials: string;
  /** Nur fuer das Team oder auch fuer Kunden? */
  audience: "intern" | "kunde";
};

export const SURFACES: readonly Surface[] = [
  {
    id: "agent",
    label: "Agent",
    short: "Agent",
    description: "Projekte, Zeiten, Angebote, Rechnungen",
    href: "/dashboard",
    initials: "AG",
    audience: "intern",
  },
  {
    id: "assets",
    label: "DAM",
    short: "DAM",
    description: "Dateien, Sammlungen, Schlagworte — je Kundeninstanz",
    href: "/dam",
    initials: "DAM",
    audience: "kunde",
  },
  {
    id: "brand",
    label: "Brand-Portal",
    short: "Brand",
    description: "Marke, Regeln, Vorlagen — je Kundeninstanz",
    href: "/brandportal",
    initials: "BP",
    audience: "kunde",
  },
] as const;

export function surfaceById(id: SurfaceId): Surface {
  const found = SURFACES.find((s) => s.id === id);
  if (!found) throw new Error(`Unbekannte Flaeche: ${id}`);
  return found;
}

/**
 * Welche Flaeche zeigt dieser Pfad? Erwartet den Pfad *inklusive*
 * basePath, so wie ihn der Browser sieht.
 */
export function surfaceForPath(pathname: string): SurfaceId {
  // `/assets` ist der Altweg der frueheren zweiten Anwendung. Er
  // leitet auf `/dam` weiter (siehe apps/erp/next.config.ts) und
  // steht hier nur, damit ein Lesezeichen unterwegs nicht die
  // falsche Flaeche markiert.
  if (pathname.startsWith("/brandportal") || pathname.startsWith("/assets/brand")) {
    return "brand";
  }
  if (pathname.startsWith("/dam") || pathname.startsWith("/assets")) return "assets";
  return "agent";
}
